<script lang="ts">
  import Button from "./Button.svelte";
  import { updates, type ReleaseUpdates } from "../release/updates.svelte";
  import { toasts } from "../stores/toasts.svelte";

  interface Props {
    store?: ReleaseUpdates;
    onPostReload?: (message: string) => void;
  }

  const { store = updates, onPostReload = (message) => toasts.info(message) }: Props = $props();
  const visible = $derived(
    store.release !== null &&
    store.severity !== null &&
    store.severity !== "silent" &&
    (store.phase === "available" || store.phase === "pending")
  );
  const title = $derived(store.severity === "required" ? "Update required" : "Update available");
  const waitingForHolds = $derived(store.phase === "pending" || (store.severity === "required" && store.held));
  const statusMessage = $derived.by(() => {
    if (waitingForHolds) return "Reload when you're ready. Your work won't be discarded.";
    if (store.severity === "required" && store.countdownSeconds !== null) {
      const unit = store.countdownSeconds === 1 ? "second" : "seconds";
      return `Reloading automatically in ${store.countdownSeconds} ${unit}.`;
    }
    return store.release?.message ?? "A new version of the portal is ready.";
  });

  $effect(() => {
    const confirmation = store.consumePostReloadConfirmation();
    if (confirmation) onPostReload(confirmation.message);
  });
</script>

{#if visible}
  <section
    class="update-toast"
    class:required={store.severity === "required"}
    role={store.severity === "required" ? "alert" : "status"}
    aria-atomic="true"
    data-focus-trap-exempt
  >
    <div class="update-copy">
      <p class="update-title">{title}</p>
      {#if store.release?.message && store.severity === "required"}
        <p class="update-message">{store.release.message}</p>
      {/if}
      <p class="update-message">{statusMessage}</p>
    </div>
    <div class="update-actions">
      {#if store.severity === "notify" && store.phase !== "pending"}
        <Button variant="ghost" size="sm" onclick={() => store.snooze()}>Later</Button>
      {/if}
      {#if store.phase !== "pending"}
        <Button size="sm" onclick={() => store.requestReload("manual")}
          >{store.severity === "required" ? "Reload now" : "Reload"}</Button
        >
      {/if}
    </div>
  </section>
{/if}

<style>
  .update-toast {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    width: min(440px, calc(100vw - 40px));
    padding: 14px 16px;
    border: 1px solid var(--accent-border);
    border-radius: var(--radius-btn);
    background: var(--surface-2);
    box-shadow: var(--shadow-md);
    animation: update-toast-in var(--dur-enter) var(--ease-out);
  }

  .update-toast.required {
    border-color: var(--warn-border);
  }

  .update-copy {
    min-width: 0;
  }

  .update-title,
  .update-message {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .update-title {
    color: var(--heading);
    font-size: 14px;
    font-weight: 700;
  }

  .update-message {
    margin-top: 3px;
    color: var(--text-soft);
    font-size: 13px;
    line-height: 1.45;
  }

  .update-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @keyframes update-toast-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
  }

  @media (max-width: 520px) {
    .update-toast {
      grid-template-columns: minmax(0, 1fr);
    }

    .update-actions {
      justify-content: flex-end;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .update-toast {
      animation: none;
    }
  }
</style>
